/**
 * @file ThemeSwitcher.jsx
 * @description A minimal theme switcher component for admin/debug use.
 * Renders a <select> dropdown to change the active theme.
 * Styled entirely with CSS custom properties — zero visual impact on existing UI.
 */

import React from 'react';
import { useTheme } from '../contexts/ThemeContext';

const switcherStyles = {
  container: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 12px',
    fontFamily: 'var(--theme-type-body-sm-font-family, Inter, system-ui, sans-serif)',
    fontSize: 'var(--theme-type-body-sm-font-size, 14px)',
    color: 'var(--theme-color-body, #4A4A6A)',
  },
  label: {
    fontWeight: 600,
    fontSize: 'var(--theme-type-caption-font-size, 12px)',
    color: 'var(--theme-color-body-mid, #7B7B9B)',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  select: {
    padding: '4px 8px',
    border: '1px solid var(--theme-color-mute, #C5C6D0)',
    borderRadius: 'var(--theme-rounded-sm, 6px)',
    backgroundColor: 'var(--theme-color-canvas, #F8F9FA)',
    color: 'var(--theme-color-ink, #1A1A2E)',
    fontFamily: 'inherit',
    fontSize: 'inherit',
    cursor: 'pointer',
    outline: 'none',
  },
};

export default function ThemeSwitcher() {
  const { activeTheme, setActiveTheme, availableThemes } = useTheme();

  const handleChange = (e) => {
    setActiveTheme(e.target.value);
  };

  return (
    <div style={switcherStyles.container}>
      <span className="hidden md:inline" style={switcherStyles.label}>Theme</span>
      <select
        style={switcherStyles.select}
        value={activeTheme}
        onChange={handleChange}
        aria-label="Select application theme"
      >
        {availableThemes.map((theme) => (
          <option key={theme.id} value={theme.id}>
            {theme.name}
          </option>
        ))}
      </select>
    </div>
  );
}
